import * as React from "react";
import { vw } from "../../../lib/utils/responsiveUtils";
import AnimatedCoverTabs from "../../atoms/tab/AnimatedConverTab";
import { useFormContext } from "react-hook-form";
import { useGetResveTimeList } from "../../../@apis/hooks/useResve";
import { useQuery } from "@tanstack/react-query";
import type { getResveTimeListGolfParkForm } from "../../../@apis/dto/request/resve.request";
import { formatKoDayDate } from "../../../lib/utils/dateUtils";
import { staticURL } from "../../../lib/utils/imageUtils";
import { useMyInfoStore } from "../../../lib/store/useMyInfoStore";

type CourseScreenProps = {
  golfParkCode?: string;
  date?: string;
};

const Z_CLASSES = ["z-40", "z-30", "z-20", "z-10"] as const;

export default function CourseScreen({ golfParkCode, date }: CourseScreenProps) {
  const { myInfo } = useMyInfoStore();
  
  const propsForm: getResveTimeListGolfParkForm = {
    golfParkCode,
    date: date ?? "",
    page: 1,
    pageSize: 1000,
  };

  const { data: resveTimeListResponse } = useQuery({
    ...useGetResveTimeList(propsForm),
    enabled: !!propsForm.golfParkCode && !!propsForm.date,
  });

  const timeListInfo = resveTimeListResponse?.data?.resveList ?? [];
  const firstItem = timeListInfo[0];

  const { setValue, watch } = useFormContext<{ selectedCourse?: string, alreadyReservedByMe?: boolean; }>();
  const selectedTime = watch("selectedCourse") || null;

  const hourSet = new Set(timeListInfo.map(item => Number(item.startTime.split(":")[0])));
  const hourList = Array.from(hourSet).sort((a, b) => a - b);

  const groupedByHour = hourList.map(hour => ({
    hour,
    items: timeListInfo.filter(item => Number(item.startTime.split(":")[0]) === hour),
  }));

  const TAB_LABELS = hourList.map(hour => ({
    label: `${hour}시`,
    value: String(hour),
  }));

  const scrollRef = React.useRef<HTMLDivElement>(null);
  const groupRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // 선택한 시간 섹션으로 스크롤
  const scrollToHour = React.useCallback((hourStr: string) => {
    const container = scrollRef.current;
    const target = groupRefs.current[hourStr];
    if (!container || !target) return;

    const top =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop;

    container.scrollTo({ top, behavior: "smooth" });
  }, []);

  const [selectedHour, setSelectedHour] = React.useState(hourList[0]?.toString() ?? "");

  React.useEffect(() => {
    if (selectedHour === "" && hourList.length > 0) {
      setSelectedHour(hourList[0].toString());
    }
  }, [hourList, selectedHour]);

  return (
    <div className="px-[15px] mb-[35px] flex flex-col h-screen pb-[180px]">
      <div className="mb-[15px]">
        {/* 날짜 */}
        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(60) }}>
          <p className="font-bold text-[#222]" style={{ fontSize: vw(16) }}>
            {formatKoDayDate(date ?? "")}
          </p>
        </div>

        <div className="mx-auto" style={{ width: vw(315), height: "1px", backgroundImage: "repeating-linear-gradient(to right,#e6e6e6 0 3px,transparent 6px 5px)", borderRadius: "2px", }} />

        {/* 골프장 정보 */}
        <div className="rounded-[15px] bg-[#f5f5f5] p-[20px] flex items-center" style={{ height: vw(124) }}>
          <div className="flex items-center gap-[19px]">
          <img
            src={
              firstItem?.golfParkImageUrl
                ? staticURL(firstItem?.golfParkImageUrl)
                : "/images/img-login-logo.webp"
            }
            alt="골프장 이미지"
            className="rounded-[10px] object-cover bg-white"
            style={{ width: vw(83), height: vw(83) }}
          />
            <div className="justify-center">
              <p className="text-[#222] font-bold" style={{ fontSize: vw(20) }}>
                {firstItem?.golfParkName}
              </p>
            </div>
          </div>
        </div>
      </div>

      <AnimatedCoverTabs
        tabs={TAB_LABELS}
        value={selectedHour}
        className="mb-[25px]"
        maxVisible={5}
        onChange={(value) => {
          setSelectedHour(value);
          setValue("selectedCourse", "");
          scrollToHour(value);
        }}
      />

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-[25px] h-screen">
        {/* 시간 그룹별 리스트 */}
        {groupedByHour.map((group) => (
          <div 
            key={group.hour} 
            ref={(el) => { groupRefs.current[String(group.hour)] = el; }}
            className="flex gap-[15px]"
          >
            {/* 시간 표시 */}
            <div className="relative w-[43px] shrink-0">
              <div className="text-[#222] font-bold text-center leading-[1.5] whitespace-pre-line" style={{ fontSize: vw(14) }}>
                {group.hour}시
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 top-7 bottom-[-15px]" style={{ width: "3px", backgroundImage: "radial-gradient(circle at center, #e6e6e6 1px, transparent 2px)", backgroundSize: "3px 6px", backgroundRepeat: "repeat-y" }} />
            </div>

            {/* 코스 리스트 */}
            <div className="flex-1 flex flex-col gap-[10px]">
              {group.items.map((item) => {
                const { resveSeq, startTime, resveTimeMemberList, golfCourseName, greenfee } = item;
                const isSelected = selectedTime === String(resveSeq);

                // 이 타임에 내가 이미 예약자로 있는지 확인
                const isAlreadyReservedByMe = resveTimeMemberList?.some(
                  (member) => member.userId === myInfo?.userId
                );

                return (
                  <div
                    key={resveSeq}
                    onClick={() => {
                      setValue("selectedCourse", String(resveSeq));
                      setValue("alreadyReservedByMe", !!isAlreadyReservedByMe);
                    }}
                    className={[
                      "w-full rounded-[15px] bg-white p-[20px]",
                      "flex flex-col justify-between",
                      "border transition-all duration-150",
                      isSelected ? "border-[#222]" : "border-[#e6e6e6]",
                    ].join(" ")}
                    style={{ height: vw(102) }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[#222] font-bold" style={{ fontSize: vw(20) }}>
                        {startTime}
                      </div>
                      {/* 참가자 아이콘 */}
                      <div className="flex items-center -space-x-[10px]">
                        {[0, 1, 2, 3].map((_, idx) => {
                          const playerItem = resveTimeMemberList[idx];
                          const isReserved = !!playerItem;

                          const bg = playerItem?.sex === 0 ? "#f95e80" : playerItem?.sex === 1 ? "#25a1d5" : "#e6e6e6";
                          const icon = !isReserved
                            ? "/icons/ic-rest.webp"
                            : playerItem?.sex === 0
                            ? "/icons/ic-female.webp"
                            : playerItem?.sex === 1
                            ? "/icons/ic-male.webp"
                            : "/icons/ic-gender.webp"; 

                          return (
                            <div
                              key={idx}
                              className={`${Z_CLASSES[idx]} rounded-full flex justify-center items-center border-2 border-white`}
                              style={{ width: vw(40), height: vw(40), backgroundColor: bg }}
                            >
                              <img
                                src={icon}
                                style={{ width: vw(26), height: vw(24) }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-[5px] text-[#888] font-[500]" style={{ fontSize: vw(12) }}>
                        <img
                          src="/icons/ic-course.webp"
                          alt=""
                          className="w-[12px] h-[12px]"
                        />
                        <span>{golfCourseName}</span>
                      </div>
                      <p className="text-[#888] font-[500]" style={{ fontSize: vw(12) }}>
                        {greenfee?.toLocaleString('ko-KR')}원
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
